import { getCookie } from 'cookies-next';
import dayjs from 'dayjs';
import { jwtDecode } from 'jwt-decode';

import { ApiResponse, ITokens } from '@/types';
import { CookieContext, setAuthCookies } from '@/utils/authCookies';
import { ACCESS_TOKEN, REFRESH_TOKEN } from '@/utils/constants';

// Support legacy/alternate env var name used in some deployments
const API_URL = process.env.NEXT_PUBLIC_API_URL || process.env.NEXT_PUBLIC_API_BASE_URL;

// Safety check for missing API URL
if (typeof window !== 'undefined' && !API_URL) {
  console.warn(
    'API base URL is not configured. Set NEXT_PUBLIC_API_URL (or NEXT_PUBLIC_API_BASE_URL). API calls will fail.'
  );
}

export class ApiResponseError extends Error {
  code = 400;
  data?: any;

  constructor(message: string, code = 400, data?: any) {
    super(message || 'Oops! Something went wrong');
    this.name = 'ApiResponseError';
    this.code = code;
    this.data = data;
  }
}

/**
 * Generate Request URL
 */
const getURL = (url: string, options?: { baseURL?: string; isMockedURL?: boolean }) => {
  const baseURL = options?.baseURL ? options.baseURL : API_URL;
  if (!baseURL) {
    throw new ApiResponseError('API URL is not configured', 500);
  }
  return baseURL + url;
};

/**
 * Refresh the access and refresh tokens, and
 * returns the new access token.
 *
 * During SSR (withAuth) the getServerSideProps context must be passed so the
 * refresh cookie can be read from the request and the new tokens are sent to
 * the browser via Set-Cookie; without it, server-side getCookie sees nothing
 * and a valid remembered session would be treated as expired.
 */
// Browser-only: parallel requests near access-token expiry share one refresh. Each refresh rotates the token,
// so N parallel refreshes would mint N sessions and leave N-1 orphaned. SSR (context set) never shares, since
// the server module is shared across users.
let refreshInFlight: Promise<string> | null = null;

export const refreshTokens = (context?: CookieContext): Promise<string> => {
  if (context || typeof window === 'undefined') return doRefreshTokens(context);
  refreshInFlight ??= doRefreshTokens().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
};

const doRefreshTokens = async (context?: CookieContext) => {
  try {
    const token = getCookie(REFRESH_TOKEN, context);
    if (!token) throw new ApiResponseError('No refresh token found', 401);

    const headers = new Headers();

    headers.append('Accept', 'application/json');
    headers.append('Content-Type', 'application/json');
    headers.append('Authorization', `Bearer ${token}`);

    // eslint-disable-next-line no-use-before-define
    const response = await fetch(getURL('/auth/refresh'), {
      method: 'GET',
      headers,
    });

    const res: ApiResponse<{ tokens: ITokens }> = await response.json();

    if (!response.ok) throw new Error(res.message);

    const { accessToken, refreshToken } = res.data.tokens;

    // A 200 that carries no token is still a failed refresh. Writing it anyway is how the
    // cookie ends up holding the string "undefined", which every later read then tries to
    // decode as a JWT — so the session breaks on the *next* request, far from the response
    // that actually caused it. Failing here routes it to the same expired-session handling
    // as any other refresh failure.
    if (typeof accessToken !== 'string' || !accessToken) {
      throw new Error('Refresh response did not include an access token');
    }

    setAuthCookies(accessToken, refreshToken, context);

    return accessToken;
  } catch (error) {
    throw new ApiResponseError('Login session expired', 401);
  }
};

/**
 * Best-effort server-side revoke of the refresh token at logout, so a copy of it stops working
 * too. Call it BEFORE clearAuthCookies(); a failure never blocks the local logout.
 */
export const revokeRefreshToken = async () => {
  const token = getCookie(REFRESH_TOKEN);
  if (typeof token !== 'string' || !token) return;
  try {
    // eslint-disable-next-line no-use-before-define
    await fetch(getURL('/auth/logout'), {
      method: 'POST',
      headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
    });
  } catch {
    // Offline logout still ends the local session.
  }
};

export type ITokenDetails = {
  email: string;
  exp: number;
  iat: number;
  sub: string;
  tokenType: 'access' | 'refresh';
  type: 'STAFF' | 'ADMIN';
};

/**
 * Checks token's validity
 */
export const getToken = async () => {
  let accessToken: string | null | true;

  try {
    accessToken = getCookie(ACCESS_TOKEN) || null;

    if (typeof accessToken === 'boolean') return null;
    if (accessToken === null) return null;
  } catch (error) {
    return null;
  }

  // The guard above proves the cookie is present, not that it is a readable JWT. An
  // unreadable one throws here, and because getToken feeds every authenticated request
  // header, that throw would break every API call rather than just this one. Reporting
  // "no usable token" sends the request out unauthenticated, which the API answers with
  // a 401 and the normal re-authentication path handles.
  let decodedToken: ITokenDetails;
  try {
    decodedToken = jwtDecode<ITokenDetails>(accessToken);
  } catch (_error) {
    return null;
  }

  const secondsUntilExpiry = dayjs.unix(decodedToken.exp).diff(dayjs(), 'seconds');
  const isExpired = secondsUntilExpiry < 60;

  if (isExpired) {
    return refreshTokens();
  }

  return accessToken;
};

/**
 * Generate HTTP headers
 */
const getHeader = async (
  headers?: Headers | Record<string, string>,
  hasFiles = false,
  auth = true
): Promise<Headers> => {
  const defaultHeaders = new Headers();

  defaultHeaders.append('Accept', 'application/json');
  defaultHeaders.append('Content-Type', 'application/json');

  if (headers) {
    // Handle different header formats
    if (headers instanceof Headers) {
      headers.forEach((value: string, key: string) => defaultHeaders.append(key, value));
    } else if (typeof headers === 'object') {
      // Handle plain object headers
      Object.entries(headers).forEach(([key, value]) => {
        if (typeof value === 'string') {
          defaultHeaders.append(key, value);
        }
      });
    }
  }

  if (hasFiles) {
    defaultHeaders.delete('Content-Type');
  }

  if (!auth) {
    return defaultHeaders;
  }

  let token: string | null;

  try {
    token = await getToken();
  } catch (error) {
    // If the error is a session expiration (401), propagate it immediately
    // rather than sending an unauthenticated request that will fail anyway.
    // Public endpoints never hit this path — getToken() returns null (not throw)
    // when no access token cookie exists.
    if (error instanceof ApiResponseError && error.code === 401) {
      throw error;
    }
    token = null;
  }

  if (token) {
    defaultHeaders.append('Authorization', `Bearer ${token}`);
  }

  return defaultHeaders;
};

/**
 * Generate HTTP body
 */
// eslint-disable-next-line no-undef
const getBody = (body?: BodyInit, hasFiles = false) => (hasFiles ? body : JSON.stringify(body));

type ErrorResponse = {
  message?: string | string[];
  code?: number;
  statusCode?: number;
  error?: string;
};

/**
 * Handle HTTP error
 */
const handleError = (httpStatusCode: number, response: unknown) => {
  if (!/^(2|3)[0-9][0-9]$/.test(String(httpStatusCode))) {
    // Extract message from NestJS error format or plain message
    let errorMessage = 'Something went wrong!!';
    const errorResponse =
      response && typeof response === 'object' ? (response as ErrorResponse) : undefined;

    // NestJS error format: { statusCode: 400, message: "...", error: "Bad Request" }
    if (errorResponse?.message) {
      if (Array.isArray(errorResponse.message)) {
        errorMessage = errorResponse.message.join(', ');
      } else if (typeof errorResponse.message === 'string') {
        errorMessage = errorResponse.message;
      }
    } else if (errorResponse?.error && typeof errorResponse.error === 'string') {
      errorMessage = errorResponse.error;
    } else if (typeof response === 'string') {
      errorMessage = response;
    } else {
      // Try to stringify the response to get some info
      try {
        const responseStr = JSON.stringify(response);
        if (responseStr && responseStr !== '{}') {
          errorMessage = `Error: ${responseStr}`;
        }
      } catch {
        errorMessage = `Request failed with status ${httpStatusCode}`;
      }
    }

    console.error('[handleError] Final error message:', errorMessage);
    throw new ApiResponseError(
      errorMessage,
      httpStatusCode ?? 501,
      response // Pass full response object to preserve custom fields
    );
  }
};

export type ResponseType = 'json' | 'blob' | 'text' | 'document';

export type HTTPOptions = {
  baseURL?: string;
  isMockedURL?: boolean;
  headers?: Headers | Record<string, string>;
  hasFiles?: boolean;
  responseType?: ResponseType;
  auth?: boolean;
  signal?: AbortSignal;
};

/**
 * HTTP GET Request
 */
const fetchGet = async <T = any>(url: string, options?: HTTPOptions) => {
  const result = await fetch(
    getURL(url, { isMockedURL: options?.isMockedURL, baseURL: options?.baseURL }),
    {
      method: 'GET',
      headers: await getHeader(options?.headers, false, options?.auth !== false),
      signal: options?.signal,
    }
  );

  let response: T;

  switch (options?.responseType) {
    case 'blob': {
      const blobResponse = await result.blob();
      response = blobResponse as unknown as T;
      break;
    }
    case 'text': {
      const textResponse = await result.text();
      response = textResponse as unknown as T;
      break;
    }
    case 'json': {
      const jsonResponse = await result.json();
      response = jsonResponse as T;
      break;
    }
    default: {
      const defaultResponse = await result.json();
      response = defaultResponse as T;
      break;
    }
  }
  handleError(result.status, response);
  return response;
};

/**
 * HTTP POST Request
 */
const fetchPost = async <T = any>(url: string, body?: unknown, options?: HTTPOptions) => {
  const result = await fetch(
    getURL(url, { isMockedURL: options?.isMockedURL, baseURL: options?.baseURL }),
    {
      method: 'POST',
      headers: await getHeader(options?.headers, options?.hasFiles, options?.auth !== false),
      // eslint-disable-next-line no-undef
      body: getBody(body as BodyInit, options?.hasFiles),
    }
  );

  let response: T;
  try {
    const contentType = result.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      response = await result.json();
    } else {
      const text = await result.text();
      // Try to parse as JSON, fallback to text message
      try {
        response = JSON.parse(text);
      } catch {
        response = { message: text || 'Unknown error occurred' } as T;
      }
    }

    // Debug logging for errors
    if (!/^(2|3)[0-9][0-9]$/.test(String(result.status))) {
      console.error('[HTTP Error] Status:', result.status);
      console.error('[HTTP Error] Response:', response);
      console.error('[HTTP Error] Response keys:', Object.keys(response || {}));
    }
  } catch (error) {
    // If reading response fails, create a generic error response
    console.error('[HTTP Error] Failed to read response:', error);
    response = { message: `Request failed with status ${result.status}: ${error}` } as T;
  }

  handleError(result.status, response);
  return response;
};

/**
 * HTTP PATCH Request
 */
const fetchPatch = async <T = any>(url: string, body?: unknown, options?: HTTPOptions) => {
  const result = await fetch(
    getURL(url, { isMockedURL: options?.isMockedURL, baseURL: options?.baseURL }),
    {
      method: 'PATCH',
      headers: await getHeader(options?.headers, options?.hasFiles, options?.auth !== false),
      // eslint-disable-next-line no-undef
      body: getBody(body as BodyInit, options?.hasFiles),
    }
  );

  const response: T = await result.json();
  handleError(result.status, response);
  return response;
};

/**
 * HTTP PUT Request
 */
const fetchPut = async <T = any>(url: string, body?: unknown, options?: HTTPOptions) => {
  const result = await fetch(
    getURL(url, { isMockedURL: options?.isMockedURL, baseURL: options?.baseURL }),
    {
      method: 'PUT',
      headers: await getHeader(options?.headers, options?.hasFiles, options?.auth !== false),
      // eslint-disable-next-line no-undef
      body: getBody(body as BodyInit, options?.hasFiles),
    }
  );

  const response: T = await result.json();
  handleError(result.status, response);
  return response;
};

/**
 * HTTP DELETE Request
 */
const fetchDelete = async <T = any>(url: string, body?: unknown, options?: HTTPOptions) => {
  const result = await fetch(
    getURL(url, { isMockedURL: options?.isMockedURL, baseURL: options?.baseURL }),
    {
      method: 'DELETE',
      headers: await getHeader(options?.headers, options?.hasFiles, options?.auth !== false),
      // eslint-disable-next-line no-undef
      body: getBody(body as BodyInit, options?.hasFiles),
    }
  );

  const response: T = await result.json();
  handleError(result.status, response);
  return response;
};

const http = {
  get: fetchGet,
  post: fetchPost,
  put: fetchPut,
  patch: fetchPatch,
  delete: fetchDelete,
};

export default http;
